import { Component, ElementRef, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AssociationHelper } from 'app/storage/association.storage';
import { Alert } from 'app/shared/alerts/simple-message/simple-message.component';
import { MeetingFormService } from '../meeting-form.service';
import { AssociationsService } from 'app/services/associations.service';
import { catchError, EMPTY, lastValueFrom } from 'rxjs';
import { SessionStorage } from 'app/storage/session.storage';
import { SopService } from 'app/services/sop.service';
import { collectFieldErrors, FieldError, focusAndScrollToField } from 'app/shared/forms/form-validation.util';
import { loadPssProjectSurvey, pssProjectSurveyRequiredKeys } from '../survey/pss-project-survey';


@Component({
  selector: 'survey-form',
  templateUrl: './survey-form.component.html',
  styleUrls: ['../meeting-form.component.scss', '../form-steps/form-steps.scss', './survey-form.component.scss']
})
export class SurveyFormComponent implements OnInit {

  rating: number = 3

  form_structure_name = "survey"
  form: FormGroup
  loading: boolean = true

  questions: Question[];
  title: string;

  showAlert: boolean = false
  alertMessage: string = ''
  alertType: Alert = Alert.SUCCESS

  toSubmit: boolean = true

  levels: string[] = []
  levelsSelected: boolean[] = []
  materials: any[] = []
  materialsQuestions: any

  submitted: boolean = false
  fieldErrors: FieldError[] = []

  // project survey of the Proposal Support Service (service2)
  isPssProject: boolean = false
  private pssRequiredKeys = new Set<string>()

  constructor(
    private formService: MeetingFormService,
    private mongoService: AssociationsService,
    private helper: AssociationHelper,
    private router: Router,
    public auth: SessionStorage,
    private fb: FormBuilder,
    private sop: SopService,
    private hostRef: ElementRef
  ) {
    
    this.isPssProject = this.auth.isProject() && this.helper.isPss()
    try{
      if(this.isPssProject){
        this.form = this.formService.getProjectSurveyForm()
        var data: any = loadPssProjectSurvey()
        this.pssRequiredKeys = new Set(pssProjectSurveyRequiredKeys(data['questions']))
      }
      else if(this.auth.isProject()){
        this.form = this.formService.getProjectSurveyForm()
        var data: any = require(`assets/meeting-form/survey_project.json`)
      }
      else{
        this.form = this.formService.getEpeSurveyForm()
        var data: any = require(`assets/meeting-form/survey_epe.json`)
      }
      this.questions = data['questions']
      this.title = data['title']
    } catch (e){
      this.questions = []
      this.title = ''
      this.form = this.fb.group({})
    }
  }

  async ngOnInit() {
    if(!this.helper.getSelectedProject() || !this.helper.getSelectedEpe()){
      this.router.navigateByUrl(`assigned-services`)
      this.loading = false
      return
    }

    if(this.auth.isProjectBoard() || this.auth.isCallManager() || this.auth.isAdmin()){
      this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:admin))`)
      this.loading = false
      return
    }
    if(this.auth.isProject()){
      var response = await lastValueFrom(this.mongoService.getProjectSurveyForm(this.helper.getSelectedProject(), this.helper.getSelectedEpe()), {defaultValue: null})
    }
    else{
      var response = await lastValueFrom(this.mongoService.getEpeSurveyForm(this.helper.getSelectedProject(), this.helper.getSelectedEpe()), {defaultValue: null})
    }

    if(!response){
      this.loading = false
      return
    }

    if(this.isPssProject){
      var form: { [key: string]: any; } = response['project_survey']
    }
    else if(this.auth.isProject()){
      var form: { [key: string]: any; } = response['project_survey']
      this.sop.getAllMaterials().subscribe(data => {      
        this.materials = data
        this.levels = this.materials.map(x => x.level).filter((v, i, a) => a.indexOf(v) === i);
        this.levelsSelected = this.levels.map(x => false)
        for(let level of this.levels){
          this.form.addControl(level, new FormArray([]))
        }
      })
    }
    else{
      var form: { [key: string]: any; } = response['epe_survey']
    }

    if(form){
      if(this.auth.isProject()){
        this.formService.updateProjectSurveyForm(form)
      }
      else{
        this.formService.updateEpeSurveyForm(form)
      }
    }
    this.loading = false
  }

  expand(level: string): void{
    this.levelsSelected[this.levels.indexOf(level)] = !this.levelsSelected[this.levels.indexOf(level)]
  }

  expandedStatus(level: string): string{
    return this.levelsSelected[this.levels.indexOf(level)] ? 'in' : ''
  }

  getQuestionsByLevel(level: string): Question{
    let trainings = this.materials.filter(x => x.level === level).map(x => x.title)
    trainings.unshift('trainings')
    let questionsLevel: Question = {}
    questionsLevel[level] = trainings
    return questionsLevel
  }

  isDropdown(question: Question) {
    return this.getQuestion(question)[0] === 'drop_down'
  }

  isOpenQuestion(question: Question) {
    return typeof question[this.getQuestionKey(question)] === 'string'
  }

  getQuestionKey(question: Question): string{
    return Object.keys(question)[0]
  }

  getQuestion(question: Question): string[] | string{
    return Object.values(question)[0]
  }

  private readonly REQUIRED_KEYS_EPE = new Set<string>([
    "How would you rank the experience with the platform? 1 (poor) – 5 (excellent)",
    "How would you rank the interaction and communication with the project representative? 1 (poor) – 5 (excellent)",
  ])

  private readonly REQUIRED_KEYS_PROJECT = new Set<string>([
    "Was the expert knowledgeable in the subject matter? 1 (strongly disagree) – 5 (strongly agree)",
    "How was your communication with the expert? 1 (difficult to understand) – 5 (easy to understand)",
    "Did the expert provide useful knowledge or recommendations? 1 (strongly disagree) – 5 (strongly agree)",
    "How would you rank the overall experience with the expert? 1 (poor) – 5 (excellent)",
    "How relevant were the expert's suggestions to your project's needs? 1 (not relevant) – 5 (highly relevant)",
    "How likely are you to implement the expert's recommendations? 1 (not likely) – 5 (highly likely)",
    "Has your project's understanding of standards and standardisation improved thanks to the HSbooster service? 1 (not at all) – 5 (very much so)",
    "Would you consider involving standards consultations like HSbooster in future proposals at:",
    "Were your queries or issues resolved effectively in a timely manner? 1 (strongly disagree) – 5 (strongly agree)",
    "How would you rank the overall HSbooster support? 1 (poor) – 5 (excellent)",
    "How likely are you to recommend HSbooster to a colleague? 1 (not likely) – 5 (very likely)",
  ])

  private get requiredKeys(): Set<string> {
    if(this.isPssProject) return this.pssRequiredKeys
    return this.auth.isProject() ? this.REQUIRED_KEYS_PROJECT : this.REQUIRED_KEYS_EPE
  }

  isFieldRequired(key: string): boolean {
    return this.requiredKeys.has(key)
  }

  getQuestionDescription(question: Question): string {
    return (question['description'] as string) || ''
  }

  showModulesSection(): boolean {
    const answer = this.form.get("Have you made use of the HSbooster training academy material?")?.value
    return Array.isArray(answer) && answer.includes('Yes')
  }

  on(i: number): string{
    if(i<=this.rating)
      return 'on'
    return ''
  }

  rate(i:number): void{
    this.rating = i
  }

  confirm(): void{

    this.submitted = true
    this.fieldErrors = collectFieldErrors(this.form, Array.from(this.requiredKeys).map(key => ({ key, label: key })))
    if(this.fieldErrors.length){
      focusAndScrollToField(this.hostRef.nativeElement, this.fieldErrors[0].key)
      return
    }

    if(this.auth.isProject()){
      this.mongoService.submitProjectSurveyForm(
        this.helper.getSelectedProject(), 
        this.helper.getSelectedEpe(), 
        this.auth.isProject() ? this.formService.getProjectSurveySubmit() : this.formService.getEpeSurveySubmit()
        )
        .pipe(
          catchError(
            err => {
              this.addAlert("Survey update failed! Please contact the administrator", Alert.ERROR)
              return EMPTY
            }
          )
        )
        .subscribe(
          then => {
            this.helper.setMeetingForm(7)
            this.addAlert("Survey succesfully updated", Alert.SUCCESS)
            this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:summary))`)
          }
        )
    }
    else{
      this.mongoService.submitEpeSurveyForm(
        this.helper.getSelectedProject(), 
        this.helper.getSelectedEpe(), 
        this.auth.isProject() ? this.formService.getProjectSurveySubmit() : this.formService.getEpeSurveySubmit()
        )
        .pipe(
          catchError(
            err => {
              this.addAlert("Survey update failed! Please contact the administrator", Alert.ERROR)
              return EMPTY
            }
          )
        )
        .subscribe(
          then => {
            this.helper.setMeetingForm(7)
            this.addAlert("Survey succesfully updated", Alert.SUCCESS)
            this.router.navigateByUrl(`form/(meeting-form:survey-form/(survey-form:summary))`)
          }
        )
      }
  }

  addAlert(message: string, type: Alert){
    this.alertMessage = message
    this.alertType = type
    this.showAlert = true
  }

  jumpToField(key: string): void {
    focusAndScrollToField(this.hostRef.nativeElement, key)
  }

}

export interface Question {
  [key:string]: string[] | string
}